import { useEffect, useState, useCallback } from 'react';
import {
  Stack,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Switch,
  TextField,
  InputAdornment,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

import { useAsignaturas, Asignatura } from '../../../hooks/useAsignaturas';
import { useTipos } from '../../../hooks/useTiposAsignatura';
import AsignaturaDialog from '../../../components/AsignaturaDialog';
import RelationDialog from '../../../components/RelationDialog';
import api from '../../../api/axios';
import { toast } from 'react-toastify';

/* — tipo de cada fila en el diálogo de relación — */
type RelItem = { id: number; nombre: string };

export default function AsignaturasPage() {
  const { t } = useTranslation<'translation'>();

  /* ─── hooks de datos ─── */
  const { rows, loading, refresh, create, update, remove, toggle } = useAsignaturas();
  const { rows: tipos, loading: tiposLoading } = useTipos();

  /* ─── diálogos y filtros ─── */
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState<Asignatura | undefined>();
  const [search, setSearch] = useState('');
  const [selectedTipo, setSelectedTipo] = useState<string>('');

  const emptyLoader = () => Promise.resolve([] as RelItem[]);
  const [relOpen, setRelOpen] = useState(false);
  const [relTitle, setRelTitle] = useState('');
  const [relLoader, setRelLoader] = useState<() => Promise<RelItem[]>>(emptyLoader);

  /* React Query carga automáticamente, no necesitamos refresh manual */

  /* ─── filtrado ─── */
  const query = search.toLowerCase();
  const filtered = rows.filter(
    (a) =>
      (a.nombre.toLowerCase().includes(query) || a.tipoId?.toString().includes(query)) &&
      (selectedTipo ? a.tipoId === Number(selectedTipo) : true),
  );

  /* ─── diálogo profesores relacionados ─── */
  const showProfes = useCallback(
    (asig: Asignatura) => {
      setRelLoader(
        () => () => api.get(`/asignaturas/${asig.id}/profesores`).then((r) => r.data as RelItem[]),
      );
      setRelTitle(t<string>('subjects.relDialog.title', { name: asig.nombre }));
      setRelOpen(true);
    },
    [t],
  );

  /* ─── acciones con manejo de errores ─── */
  const handleRemove = async (id: number) => {
    try {
      await remove(id);
    } catch {
      toast.error(t('subjects.toast.deletePerm'));
    }
  };

  const handleToggle = async (id: number, activar: boolean) => {
    try {
      await toggle(id, activar);
    } catch {
      toast.error(t('subjects.toast.togglePerm'));
    }
  };

  /* ════════════════ UI ════════════════ */
  return (
    <Stack spacing={2}>
      <Typography variant="h5">{t('subjects.title')}</Typography>

      <Button
        variant="contained"
        onClick={() => {
          setEditing(undefined);
          setDlgOpen(true);
        }}
      >
        {t('subjects.button.new')}
      </Button>

      {/* buscador */}
      <TextField
        placeholder={t<string>('subjects.search.placeholder')}
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ maxWidth: 320 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* filtro por tipo */}
      <FormControl size="small" sx={{ maxWidth: 200 }}>
        <InputLabel>{t('subjects.filter.type.label')}</InputLabel>
        <Select
          value={selectedTipo}
          label={t('subjects.filter.type.label')}
          onChange={(e) => setSelectedTipo(e.target.value)}
        >
          <MenuItem value="">{t('subjects.filter.type.all')}</MenuItem>
          {tipos.map((tipo) => (
            <MenuItem key={tipo.id} value={tipo.id}>
              {tipo.nombre}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* tabla */}
      {loading || tiposLoading ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress />
        </Stack>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('subjects.table.name')}</TableCell>
              <TableCell>{t('subjects.table.type')}</TableCell>
              <TableCell align="center">{t('subjects.table.active')}</TableCell>
              <TableCell align="center">{t('subjects.table.noRequireStudent')}</TableCell>
              <TableCell align="center">
                {t('subjects.table.excludeSignReports', {
                  defaultValue: 'Excluir de informes firmas',
                })}
              </TableCell>
              <TableCell width={120} />
            </TableRow>
          </TableHead>

          <TableBody>
            {filtered.map((a) => (
              <TableRow key={a.id}>
                <TableCell>{a.nombre}</TableCell>

                <TableCell>{tipos.find((tipo) => tipo.id === a.tipoId)?.nombre || '—'}</TableCell>

                <TableCell align="center">
                  <Switch checked={a.activo} onChange={(_, v) => handleToggle(a.id, v)} />
                </TableCell>
                <TableCell align="center">
                  <Switch
                    checked={!!a.noRequiereAlumno}
                    onChange={async (_, v) => {
                      try {
                        await update(a.id, { noRequiereAlumno: v } as any);
                      } catch {
                        toast.error(t('subjects.toast.togglePerm'));
                      }
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Switch
                    checked={!!a.excluirInformesFirmas}
                    onChange={async (_, v) => {
                      try {
                        await update(a.id, { excluirInformesFirmas: v } as any);
                      } catch {
                        toast.error(t('subjects.toast.togglePerm'));
                      }
                    }}
                  />
                </TableCell>

                <TableCell align="right">
                  <Tooltip title={t('subjects.tooltip.professors')}>
                    <IconButton size="small" onClick={() => showProfes(a)}>
                      <PeopleAltIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={t('common.actions.edit')}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditing(a);
                        setDlgOpen(true);
                      }}
                    >
                      <EditIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={t('common.actions.delete')}>
                    <IconButton size="small" onClick={() => handleRemove(a.id)}>
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {!filtered.length && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {t('subjects.table.noResults')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {/* alta / edición */}
      <AsignaturaDialog
        open={dlgOpen}
        initial={editing}
        onClose={() => setDlgOpen(false)}
        onSave={(data) => {
          // El diálogo devuelve Omit<Asignatura, 'id' | 'tipo'>, pero el hook acepta Omit<Asignatura, 'id'>
          // Como 'tipo' es opcional y no se envía al backend, podemos hacer un cast seguro
          editing
            ? update(editing.id, data as Omit<Asignatura, 'id'>)
            : create(data as Omit<Asignatura, 'id'>);
          setDlgOpen(false);
        }}
      />

      {/* diálogo relaciones */}
      <RelationDialog
        open={relOpen}
        title={relTitle}
        loader={relLoader}
        onClose={() => setRelOpen(false)}
      />
    </Stack>
  );
}
